import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiResponseError, DeepPartial, IAdminProfile, IEntities, IEntity, IFormGroupSchema } from '@lib/interfaces';
import { AngularMaterialModule } from '../../../../material.module';
import { ValueFieldModule } from '@lib/modules';
import { BaseComponent, StatusComponent } from '@lib/components';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccessManagementService } from '@lib/services';
import { CardStatusModalComponent } from '../card-status-modal/card-status-modal.component';
import { statusReasonInfo } from '@lib/enums/cards.enum';
import { ICard, ICardStatusChange } from '@lib/interfaces/card.interface';
import { CardLessService } from '@lib/services/card-less/card-less.service';
import { tenantSettings } from 'src/theme';
import { NgOptimizedImage } from '@angular/common';

export type IMutateCardStatusChange = IFormGroupSchema<DeepPartial<ICardStatusChange>>;

@Component({
    selector: 'app-advanced-search-modal',
    standalone: true,
    imports: [AngularMaterialModule, ReactiveFormsModule, ValueFieldModule, StatusComponent, NgOptimizedImage],
    templateUrl: './change-card-status-modal.component.html',
    styleUrls: ['./change-card-status-modal.component.scss'],
})
export class ChangeCardStatusModalComponent extends BaseComponent implements OnInit {
    public cardStatusForm: FormGroup<IMutateCardStatusChange>;
    protected statusReasonInfo: IEntities = statusReasonInfo;
    protected statusReasonDropdown: IEntities = [];
    protected newSelectStatus = '';
    protected isLoading = false;
    protected userId = localStorage.getItem('id');
    protected tenantKey: string;
    protected isNeoTenant: boolean;
    protected isICTenant: boolean;
    protected isRiyadTenant: boolean;
    protected user_name = '';
    constructor(
        public dialogRef: MatDialogRef<ChangeCardStatusModalComponent>,
        private readonly dialog: MatDialog,
        private formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public cardItem: ICard,
        private cardsService: CardLessService,
        private _snackbar: MatSnackBar,
        public accessManagementService: AccessManagementService,
    ) {
        super();
        const tenantKey = localStorage.getItem('tenantKey') || '';
        const riyadTenant = tenantSettings.find((tenant) => tenant.name.toLowerCase() === 'riyad');
        this.isRiyadTenant = tenantKey === riyadTenant?.key;
    }

    ngOnInit(): void {
        this.fetchAdminProfileDetails();
        this.cardStatusForm = this.createForm();
    }

    protected createForm(): FormGroup<IMutateCardStatusChange> {
        // For Riyad tenant, exclude status_reason_code from the form
        if (this.isRiyadTenant) {
            return this.formBuilder.group<IMutateCardStatusChange>({
                comment: this.formBuilder.nonNullable.control('', []),
            });
        }

        // For all other tenants, keep it as before
        return this.formBuilder.group<IMutateCardStatusChange>({
            status_reason_code: this.formBuilder.nonNullable.control({ value: undefined, disabled: true }, [Validators.required, Validators.maxLength(6)]),
            comment: this.formBuilder.nonNullable.control('', []),
        });
    }

    protected openCardStatusModal() {
        const dialogRef = this.dialog.open(CardStatusModalComponent, {
            backdropClass: 'transparent-backdrop',
            panelClass: 'card-status-modal',
            width: '150px',
            data: this.cardItem.status,
        });

        dialogRef.afterClosed().subscribe((status) => {
            if (status) {
                this.newSelectStatus = status;
              
                if (!this.isRiyadTenant) {
                  this.cardStatusForm.controls.status_reason_code?.reset();
                  this.cardStatusForm.controls.status_reason_code?.enable();
              
                  const activeDropdownValues = this.validateDropdownCardReason(status);
                  this.statusReasonDropdown = this.statusReasonInfo.filter((item: IEntity) =>
                    activeDropdownValues.includes(item.id)
                  );
              
                  if (this.statusReasonDropdown.length > 0) {
                    this.cardStatusForm.controls.status_reason_code?.setValue(this.statusReasonDropdown[0].id);
                  }
                }
              }
        });
    }

    validateDropdownCardReason(selectedStatus: string) {
        let activeReasons: string[] = [];
        if (this.cardItem.status === 'INACTIVE' && selectedStatus === 'ACTIVE') {
            activeReasons = ['A0'];
        }
        if (this.cardItem.status === 'SUSPENDED' && selectedStatus === 'ACTIVE') {
            activeReasons = this.isNeoTenant || this.isICTenant ? ['A1', 'A3'] : ['A1'];
        }
        if (selectedStatus === 'SUSPENDED') {
            activeReasons = this.isNeoTenant || this.isICTenant ? ['S0', 'S4'] : ['S0'];
        }
        if (selectedStatus === 'TERMINATED') {
            activeReasons = ['T0', 'T1', 'T2', 'T3', 'T4', 'T5'];
        }
        return activeReasons;
    }

    protected onSubmit() {
        this.isLoading = true;

        let selectedReasonCode = '';

        if (this.isRiyadTenant) {
            const validReasons = this.validateDropdownCardReason(this.newSelectStatus);
            selectedReasonCode = validReasons[0] || '';
        } else if (this.cardStatusForm.controls.status_reason_code) {
            selectedReasonCode = this.cardStatusForm.controls.status_reason_code.value || '';
        }

        const payload: DeepPartial<ICardStatusChange> = {
            status: this.newSelectStatus,
            status_reason_code: selectedReasonCode,
            modified_by: this.user_name,
            comment: this.cardStatusForm.get('comment')?.value || '',
        };

        const isRestrictedStatus = ['S4', 'A3'].includes(selectedReasonCode);

        this.cardsService.changeCardStatus(this.cardItem.id, payload, isRestrictedStatus).subscribe({
            next: (value) => {
                this.dialogRef.close(value);
                this.isLoading = false;
            },
            error: (error: ApiResponseError) => {
                this.isLoading = false;
                this.createApiErrorSnackBar(this._snackbar, error);
            },
        });
    }

    fetchAdminProfileDetails(): void {
        if (!this.userId) return;

        this.addSubscription(
            this.accessManagementService.fetchUserById(this.userId).subscribe({
                next: (adminData: IAdminProfile) => {
                    this.user_name = adminData.first_name + ' ' + adminData.last_name;
                },
                error: (error: ApiResponseError) => {
                    console.log(error);
                },
            }),
        );
    }
}
